import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardProps,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { CardTitle } from '@sierralabs/core-ui';

import CardContent from '../../../common/card/CardContent';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import AppSnackbar from '../../../common/AppSnackbar';
import { useDebounce } from '../../../common/Debounce.hooks';
import StatusLabel from '../../../common/StatusLabel';
import { convertJiraStatus } from '../../jira/Jira.hooks';
import JiraLink from '../../jira-connect/components/JiraLink';
import JiraRenderedField from '../../jira-connect/components/JiraRenderedField';
import JiraIssueCreateDialog from '../../jira-connect/dialogs/JiraIssueCreateDialog';
import { useFindJiraIssues } from '../../jira-connect/JiraConnect.hooks';
import { JiraIssue } from '../../jira-connect/JiraConnect.interface';
import { useJiraServerContext } from '../../jira-server/jira-server.context';
import { JiraFieldConfig } from '../../jira-server/jira-server.interface';
import { TaskV2 } from '../../task-v2/TaskV2.interface';
import {
  useCreateTaskJiraIssueV2,
  useDeleteTaskJiraIssueV2,
  useFindTaskJiraIssuesV2
} from '../TaskJiraIssueV2.hooks';
import { TaskJiraIssueV2 } from '../TaskJiraIssueV2.interface';

export interface TaskJiraV2AssignCardProps {
  task: TaskV2 | undefined; // undefined for site builder view mode
  icon?: string;
  title?: React.ReactNode;
  entity?: { name: string; description?: string };
  jiraFieldColumns?: JiraFieldConfig[];
  className?: string;
  cardProps?: CardProps;
  onChange?: (jiraIssues: TaskJiraIssueV2[]) => void;
}

const TaskJiraV2AssignCard: React.FC<TaskJiraV2AssignCardProps> = props => {
  const {
    task,
    icon = 'confirmation_number',
    title = 'Linked Jira Issues',
    entity,
    className,
    jiraFieldColumns,
    cardProps,
    onChange
  } = props;
  const taskId = task?.id;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search || '', 300);
  const [selectedIssue, setSelectedIssue] = useState<JiraIssue | null>(null);
  const [issueToUnlink, setIssueToUnlink] = useState<TaskJiraIssueV2>();
  const [didCreateIssue, setDidCreateIssue] = useState<{
    open: boolean;
    key?: string;
  }>({ open: false });
  const [openCreateJiraIssueDialog, setOpenCreateJiraIssueDialog] =
    useState(false);
  const { isJiraServer, baseUrl } = useJiraServerContext();
  const [{ data: jiraIssues }, findIssues] = useFindJiraIssues();
  const [{ data: updatedJiraIssues }, findIssuesByIds] = useFindJiraIssues();
  const { taskJiraIssues, findTaskJiraIssues } = useFindTaskJiraIssuesV2(
    taskId || 0
  );
  const {
    data: createdTaskJiraIssue,
    isLoading: isCreating,
    createTaskJiraIssue
  } = useCreateTaskJiraIssueV2();
  const { deletedJiraIssue, deleteTaskJiraIssue } = useDeleteTaskJiraIssueV2(
    issueToUnlink?.id || 0
  );

  // get the summary field from jira data in case our local cache is outdated
  const mergedTaskJiraIssues = useMemo(() => {
    if (!taskJiraIssues || !updatedJiraIssues) {
      return taskJiraIssues;
    }
    return taskJiraIssues.map(issue => {
      const jiraIssue = updatedJiraIssues.data.find(i => i.key === issue.key);
      return {
        ...issue,
        summary: jiraIssue?.fields.summary || issue.summary,
        status: jiraIssue?.fields.status?.name || issue.status
      };
    });
  }, [taskJiraIssues, updatedJiraIssues]);

  // remove selectedIssue on select to re-render field
  useEffect(() => {
    selectedIssue && setSelectedIssue(null);
  }, [selectedIssue]);

  // fetch when task.status gets updated after creating Jira issue on transition
  useEffect(() => {
    taskId && findTaskJiraIssues();
  }, [
    findTaskJiraIssues,
    taskId,
    task?.status,
    createdTaskJiraIssue,
    deletedJiraIssue
  ]);

  useEffect(() => {
    if (taskJiraIssues) {
      const keys = taskJiraIssues.map(i => i.key);
      findIssuesByIds({ keys, limit: keys.length > 0 ? 10 : 0 });
    }
  }, [taskJiraIssues, findIssuesByIds]);

  useEffect(() => {
    findIssues({ limit: 100, title: debouncedSearch });
  }, [debouncedSearch, findIssues]);

  useEffect(() => {
    if (mergedTaskJiraIssues) {
      onChangeRef.current?.(mergedTaskJiraIssues);
    }
  }, [mergedTaskJiraIssues]);

  return (
    <Card className={className} {...cardProps}>
      <CardTitle>
        <Icon className='icon'>{icon}</Icon>
        {title}
      </CardTitle>
      <CardContent>
        <Autocomplete<JiraIssue>
          fullWidth
          clearOnBlur
          value={selectedIssue}
          disabled={isCreating}
          options={jiraIssues?.data || []}
          getOptionLabel={issue => `${issue.key}: ${issue.fields.summary}`}
          renderInput={params => (
            <TextField
              {...params}
              size='small'
              variant='outlined'
              placeholder='Link a Jira Issue'
            />
          )}
          onInputChange={(_, value) => setSearch(value)}
          onChange={(_, issue) => {
            setSelectedIssue(issue);
            if (
              !task ||
              !issue ||
              mergedTaskJiraIssues?.some(i => i.key === issue.key)
            ) {
              return;
            }
            createTaskJiraIssue({
              taskId: task.id,
              issue,
              key: issue.key,
              summary: issue.fields.summary,
              status: issue.fields.status.name
            });
          }}
        />
      </CardContent>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Status</TableCell>
              {jiraFieldColumns?.map(column => (
                <TableCell key={column.fieldKey}>{column.title}</TableCell>
              ))}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedTaskJiraIssues?.map(taskJiraIssue => {
              const status = taskJiraIssue.status || 'Backlog';
              return (
                <TableRow key={taskJiraIssue.id}>
                  <TableCell>
                    <JiraLink issue={taskJiraIssue} />
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'normal' }}>
                    {taskJiraIssue.summary}
                  </TableCell>
                  <TableCell>
                    <StatusLabel status={convertJiraStatus(status)} />
                  </TableCell>
                  {jiraFieldColumns?.map(column => (
                    <TableCell key={`${column.fieldKey}-${taskJiraIssue.id}`}>
                      <JiraRenderedField
                        config={column}
                        baseUrl={baseUrl}
                        isJiraServer={isJiraServer}
                        issue={taskJiraIssue.issue}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton
                      size='small'
                      onClick={() => setIssueToUnlink(taskJiraIssue)}
                    >
                      <Close />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CardActions>
        <Button
          color='primary'
          variant='outlined'
          disabled={!task}
          onClick={() => setOpenCreateJiraIssueDialog(true)}
        >
          Create Jira Issue
        </Button>
      </CardActions>

      <ConfirmDialog
        open={!!issueToUnlink}
        confirmLabel='Confirm'
        title={`Unlink Issue ${issueToUnlink?.key || '?'}`}
        message='Are you sure you want to unlink this issue?'
        onCancel={() => setIssueToUnlink(undefined)}
        onConfirm={() => {
          issueToUnlink && deleteTaskJiraIssue();
          setIssueToUnlink(undefined);
        }}
      />
      {task && (
        <JiraIssueCreateDialog
          open={openCreateJiraIssueDialog}
          entity={{
            taskId: task.id,
            title: entity?.name || task.name,
            description: entity?.description || task.description
          }}
          onCancel={() => setOpenCreateJiraIssueDialog(false)}
          onCreate={(issue, shouldLink) => {
            setOpenCreateJiraIssueDialog(false);
            setDidCreateIssue({ open: true, key: issue.key });
            if (shouldLink) {
              createTaskJiraIssue({
                taskId: task.id,
                issue,
                key: issue.key,
                summary: issue.fields.summary,
                status: issue.fields.status.name
              });
            }
          }}
        />
      )}
      <AppSnackbar
        severity='success'
        open={didCreateIssue.open}
        onClose={() => setDidCreateIssue(value => ({ ...value, open: false }))}
      >
        Created Jira Issue: {didCreateIssue.key}
      </AppSnackbar>
    </Card>
  );
};

export default TaskJiraV2AssignCard;
